"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ProfileCollectionListingsPanel } from "@/components/feed/discover-category-panel";
import type { ListingItem } from "@/hooks/use-paginated-feed";
// `Z.overlayPanel` for the numeric value only — this slider keeps its own
// local `open` prop/state (not migrated onto OverlayContext, out of §E's
// scope); see lib/overlay/z-scale.ts's doc-comment (step 1.8).
import { Z } from "@/lib/overlay/z-scale";

export type ProfileCollectionSliderListing = {
  id: string;
  title: string;
  description: string;
  price: string;
  currency?: string | null;
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: structured trio replaces
  // free-text `location`. The downstream `ListingItem` shape (consumer
  // `ProfileCollectionListingsPanel`) reads `shipFromAddress`.
  shipFromAddress?: { city: string | null; state: string | null; country: string | null } | null;
  image_urls: string[];
  created_at: string;
  condition: string;
  grade: string;
  quantity: number;
  username?: string;
  avatar_url?: string | null;
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  user_id?: string;
};

export interface ProfileCollectionForSlider {
  id: string;
  title: string;
  description?: string | null;
  listings: ProfileCollectionSliderListing[];
}

function toListingItems(listings: ProfileCollectionSliderListing[]): ListingItem[] {
  return listings.map((l) => ({
    id: l.id,
    title: l.title,
    description: l.description,
    price: l.price,
    currency: l.currency ?? null,
    image_urls: l.image_urls,
    shipFromAddress: l.shipFromAddress ?? null,
    condition: l.condition,
    grade: l.grade,
    created_at: l.created_at,
    user_id: l.user_id ?? "",
    quantity: l.quantity,
    sellerUsername: l.username,
    sellerAvatarUrl: l.avatar_url ?? undefined,
    initialSaved: false,
    scheduled_publish_at: l.scheduled_publish_at ?? null,
    listing_format: l.listing_format ?? null,
    auction_start_price: null,
    auction_highest_bid: null,
    auction_end_at: null,
    auction_status: null,
    auction_bid_count: null,
  }));
}

interface ProfileCollectionSliderProps {
  open: boolean;
  onOpenChangeAction: (open: boolean) => void;
  collection: ProfileCollectionForSlider | null;
  currentUserId?: string;
  sellerBanned?: boolean;
  isOwner?: boolean;
  onCollectionTitleChange?: (collectionId: string, newTitle: string) => Promise<void>;
  onCollectionDescriptionChange?: (collectionId: string, newDescription: string) => Promise<void>;
  onListingRemovedFromCollection?: (collectionId: string, listingId: string) => void;
  onAddItems?: () => void;
}

export function ProfileCollectionSlider({
  open,
  onOpenChangeAction,
  collection,
  currentUserId,
  sellerBanned = false,
  isOwner = false,
  onCollectionTitleChange,
  onCollectionDescriptionChange,
  onListingRemovedFromCollection,
  onAddItems,
}: ProfileCollectionSliderProps) {
  const [mounted, setMounted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [open]);

  const listingItems = collection ? toListingItems(collection.listings) : [];

  const handleClose = () => onOpenChangeAction(false);

  const content = (
    <AnimatePresence>
      {open && collection ? (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="fixed top-0 right-0 flex h-dvh w-full flex-col bg-background"
          style={{ zIndex: Z.overlayPanel }}
        >
          <div
            ref={scrollRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain pb-[calc(3.5rem+var(--kraahl-safe-bottom))]"
          >
            <div className="mx-auto w-full max-w-(--breakpoint-lg) md:max-w-7xl xl:max-w-[min(100%,90rem)]">
              <ProfileCollectionListingsPanel
                name={collection.title}
                collectionId={collection.id}
                collectionDescription={collection.description}
                listings={listingItems}
                currentUserId={currentUserId}
                sellerBanned={sellerBanned}
                isOwner={isOwner}
                onBack={handleClose}
                scrollRootRef={scrollRef}
                compactGutter
                onCollectionTitleChange={onCollectionTitleChange}
                onCollectionDescriptionChange={onCollectionDescriptionChange}
                onListingRemovedFromCollection={onListingRemovedFromCollection}
                onAddItems={onAddItems}
              />
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
